import React from 'react';
import { Box, Stack, Tooltip, Typography, alpha } from '@mui/material';
import { EventBusy } from '@mui/icons-material';
import type { StudentReportData } from '../../../services/analyticsService';
import { AttendanceStatus } from '../../../types';
import { STATUS_VISUAL, attendanceRateColor } from '../../../utils/attendanceStatus';
import { useTimezone } from '../../../hooks/useTimezone';
import { SectionCard } from '../../common/SectionCard';
import EmptyState from '../../common/EmptyState';

type Counted = { total: number; informed: number; uninformed: number };

/** One line of the breakdown: a coloured dot, what it is, informed / uninformed under it, and the count. */
const Line: React.FC<{ label: string; value: number; color: string; split?: Counted }> = ({ label, value, color, split }) => (
  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ py: 0.75 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2">{label}</Typography>
      {split && split.total > 0 && (
        <Typography variant="caption" color="text.secondary">{split.informed} informed · {split.uninformed} uninformed</Typography>
      )}
    </Box>
    <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
  </Stack>
);

/** The session's attendance: the rate, what the school days were, and the last ten marked days. */
const AttendanceCard: React.FC<{ report: StudentReportData }> = ({ report }) => {
  const { formatDate } = useTimezone();
  const a = report.administrativeData.attendanceOverview;

  if (a.totalDays === 0) {
    return (
      <SectionCard title="Attendance">
        <EmptyState dense icon={<EventBusy />} title="Nothing marked yet" description="Attendance for this session appears here once it is taken." />
      </SectionCard>
    );
  }

  const color = attendanceRateColor(a.attendanceRate);
  const visual = (s: string) => STATUS_VISUAL[s as AttendanceStatus];
  return (
    <SectionCard title="Attendance">
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="h3" fontWeight={700} sx={{ color, lineHeight: 1 }}>{a.attendanceRate}%</Typography>
        <Typography variant="body2" color="text.secondary">of {a.totalDays} school days</Typography>
      </Stack>
      <Box sx={{ height: 6, borderRadius: 3, bgcolor: alpha(color, 0.15), overflow: 'hidden', mb: 1.5 }}>
        <Box sx={{ width: `${a.attendanceRate}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
      </Box>

      <Box sx={{ '& > div + div': { borderTop: 1, borderColor: 'divider' } }}>
        <Line label="Present" value={a.present} color={STATUS_VISUAL[AttendanceStatus.PRESENT].bg} />
        <Line label="Late" value={a.late.total} color={STATUS_VISUAL[AttendanceStatus.LATE].bg} split={a.late} />
        <Line label="Half day" value={a.halfDay.total} color={STATUS_VISUAL[AttendanceStatus.HALF_DAY].bg} split={a.halfDay} />
        <Line label="Absent" value={a.absent.total} color={STATUS_VISUAL[AttendanceStatus.ABSENT].bg} split={a.absent} />
      </Box>

      {a.recentPattern.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Last {Math.min(10, a.recentPattern.length)} days</Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            {a.recentPattern.slice(0, 10).reverse().map(r => {
              const v = visual(r.status);
              const informed = r.informationType ? ` · ${r.informationType === 'INFORMED' ? 'informed' : 'uninformed'}` : '';
              return (
                <Tooltip key={r.date} title={`${formatDate(r.date)} — ${v?.label ?? r.status}${informed}`} arrow>
                  <Box sx={{
                    minWidth: 26, height: 26, px: 0.5, borderRadius: 1, display: 'grid', placeItems: 'center',
                    bgcolor: v?.bg ?? 'action.disabled', color: '#fff', fontSize: '0.68rem', fontWeight: 700, cursor: 'default',
                  }}>
                    {v?.text ?? '?'}
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        </Box>
      )}
    </SectionCard>
  );
};

export default AttendanceCard;
